'use client';

import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';
import {
  BaseNotificationPayload,
  MeetEnterRequestMessage,
  MeetEnterAllowedMessage,
  MeetEnterDeniedMessage,
  NotificationItem,
  NotificationState,
  NOTIFICATION_EVENTS,
  MeetUpgradeMessage,
  MeetRaiseHandMessage,
  MeetLowerHandMessage,
  MeetSmartCoachInsightMessage,
  MeetParticipantsInvitedMessage,
} from './types';
import { getEnv } from '@/lib/config/runtimeConfig';

// Config
const HEARTBEAT_INTERVAL = 30000;
const MAX_NOTIFICATIONS = 50;

const getBaseUrl = () => {
  const url =
    getEnv('NEXT_PUBLIC_NOTIFICATION_BASE_URL') || 'https://notification-dev.smartspace.us';
  if (!url) throw new Error('NEXT_PUBLIC_NOTIFICATION_BASE_URL is not set');
  return url;
};

/**
 * Parseia a mensagem JSON dentro do payload de notificaÃƒÂ§ÃƒÂ£o
 */
const parseNotificationMessage = (
  eventName: string,
  message: string,
):
  | MeetEnterRequestMessage
  | MeetEnterAllowedMessage
  | MeetEnterDeniedMessage
  | MeetUpgradeMessage
  | MeetRaiseHandMessage
  | MeetLowerHandMessage
  | MeetParticipantsInvitedMessage
  | MeetSmartCoachInsightMessage
  | undefined => {
  try {
    const parsed = JSON.parse(message);

    if (eventName === NOTIFICATION_EVENTS.MEET_ENTER_REQUEST) {
      return parsed as MeetEnterRequestMessage;
    }

    if (eventName === NOTIFICATION_EVENTS.MEET_ENTER_ALLOWED || eventName === NOTIFICATION_EVENTS.MEET_CHAT_READY) {
      return parsed as MeetEnterAllowedMessage;
    }

    if (eventName === NOTIFICATION_EVENTS.MEET_ENTER_DENIED) {
      return parsed as MeetEnterDeniedMessage;
    }

    if (eventName === NOTIFICATION_EVENTS.MEET_UPGRADE) {
      return parsed as MeetUpgradeMessage;
    }

    if (eventName === NOTIFICATION_EVENTS.MEET_RAISE_HAND) {
      return parsed as MeetRaiseHandMessage;
    }

    if (eventName === NOTIFICATION_EVENTS.MEET_LOWER_HAND) {
      return parsed as MeetLowerHandMessage;
    }

    if (eventName === NOTIFICATION_EVENTS.MEET_SMARTCOACH_INSIGHT) {
      return parsed as MeetSmartCoachInsightMessage;
    }

    if (eventName === NOTIFICATION_EVENTS.MEET_PARTICIPANTS_INVITED) {
      return parsed as MeetParticipantsInvitedMessage;
    }

    // Adicione novos parsers aqui para outros tipos de notificaÃƒÂ§ÃƒÂ£o

    return parsed;
  } catch {
    return undefined;
  }
};

/**
 * Processa o payload bruto do WebSocket em um NotificationItem
 */
const processNotification = (event: string, data: BaseNotificationPayload): NotificationItem => {
  const parsedMessage = parseNotificationMessage(event, data.message);

  return {
    id: data.id,
    event,
    payload: data,
    parsedMessage,
    timestamp: new Date(data.createdAt || Date.now()),
    dismissed: false,
  };
};

export const isVisualNotification = (notification: NotificationItem): boolean => {
  return (
    notification.event !== NOTIFICATION_EVENTS.MEET_UPGRADE &&
    !(notification.payload.type === 'SYSTEM' && notification.payload.category === 'SYSTEM')
  );
};

export const isNotificationPayload = (data: unknown): data is BaseNotificationPayload => {
  if (!data || typeof data !== 'object') return false;

  const payload = data as Partial<BaseNotificationPayload>;
  return (
    typeof payload.id === 'string' &&
    typeof payload.title === 'string' &&
    typeof payload.message === 'string' &&
    typeof payload.eventName === 'string' &&
    typeof payload.type === 'string' &&
    typeof payload.priority === 'string' &&
    typeof payload.category === 'string'
  );
};

const getNotificationLogContext = (
  event: string,
  payload: Partial<BaseNotificationPayload>,
) => ({
  id: payload.id,
  event,
  eventName: payload.eventName,
  type: payload.type,
  category: payload.category,
  priority: payload.priority,
  title: payload.title,
  hasMessage: Boolean(payload.message),
});

// Store
export const useNotificationStore = create<NotificationState>((set, get) => {
  let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  let roomSubscription: { userId: string; roomId: string } | null = null;

  const startHeartbeat = () => {
    stopHeartbeat();
    heartbeatInterval = setInterval(() => {
      const { socket, isRegistered } = get();
      if (socket?.connected && isRegistered) {
        socket.emit('heartbeat', { timestamp: Date.now() });
      }
    }, HEARTBEAT_INTERVAL);
  };

  const stopHeartbeat = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  };

  const registerRoom = (socket: Socket, userId: string, roomId: string) => {
    if (socket.connected) {
      socket.emit('subscribe', { type: 'USER', id: `${userId}#${roomId}` });
    }
  };

  const unregisterRoom = (socket: Socket, userId: string, roomId: string) => {
    if (socket.connected) {
      socket.emit('unsubscribe', { type: 'USER', id: `${userId}#${roomId}` });
    }
  };

  const calculateUnreadCount = (notifications: NotificationItem[]): number => {
    return notifications.filter(
      (n) => !n.dismissed && n.payload.type !== 'SILENT' && isVisualNotification(n),
    ).length;
  };

  return {
    // Initial state
    status: 'disconnected',
    socket: null,
    isRegistered: false,
    lastNotification: null,
    notifications: [],
    unreadCount: 0,

    // Actions
    connect: (userId: string) => {
      if (typeof window === 'undefined') return;

      const { socket: existingSocket, disconnect } = get();
      if (existingSocket) disconnect();

      set({ status: 'connecting' });

      const socket = io(`${getBaseUrl()}/notifications`, {
        transports: ['websocket', 'polling'],
        path: '/socket.io',
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelayMax: 10000,
        reconnectionDelay: 1000,
      });

      // Connection events
      socket.on('connect', () => {
        set({ status: 'connected' });
        if (roomSubscription) {
          registerRoom(socket, roomSubscription.userId, roomSubscription.roomId);
        }
      });

      socket.on('disconnect', () => {
        set({ status: 'disconnected', isRegistered: false });
        stopHeartbeat();
      });

      socket.on('subscribed', () => {
        set({ isRegistered: true });
        startHeartbeat();
      });

      // Notification handler
      socket.onAny((event: string, data: BaseNotificationPayload) => {
        // Ignore internal socket.io events and events not relevant to Meet
        if (
          [
            'connect',
            'disconnect',
            'subscribed',
            'unsubscribed',
            'heartbeat',
            'agent-presence-status',
          ].includes(
            event,
          )
        )
          return;

        if (!isNotificationPayload(data)) {
          console.warn('[notifications] Ignored socket event with invalid payload', { event });
          return;
        }

        console.info('[notifications] Received notification from socket',
          getNotificationLogContext(event, data),
        );

        const notification = processNotification(event, data);

        if (
          event === NOTIFICATION_EVENTS.MEET_ENTER_REQUEST_RESOLVED ||
          event === NOTIFICATION_EVENTS.MEET_PARTICIPANT_JOINED
        ) {
          const resolved = notification.parsedMessage as {
            roomId?: string;
            requesterId?: string;
            participantId?: string;
          } | undefined;
          const requesterId = resolved?.requesterId ?? resolved?.participantId;

          if (resolved?.roomId && requesterId) {
            set((state) => {
              const notifications = state.notifications.filter((item) => {
                const request = item.parsedMessage as MeetEnterRequestMessage | undefined;
                const isResolvedRequest =
                  item.event === NOTIFICATION_EVENTS.MEET_ENTER_REQUEST &&
                  request?.roomId === resolved.roomId &&
                  request?.requesterId === requesterId;
                const isExternalEntryNotice =
                  event === NOTIFICATION_EVENTS.MEET_PARTICIPANT_JOINED &&
                  item.event === NOTIFICATION_EVENTS.MEET_ENTER_EXTERNAL_USER &&
                  request?.roomId === resolved.roomId;

                return !isResolvedRequest && !isExternalEntryNotice;
              });
              return {
                lastNotification: notification,
                notifications,
                unreadCount: calculateUnreadCount(notifications),
              };
            });
          }
          console.info('[notifications] Processed room-entry lifecycle event', {
            ...getNotificationLogContext(event, data),
            roomId: resolved?.roomId,
            requesterId,
          });
          return;
        }

        if (event === NOTIFICATION_EVENTS.MEET_ENTER_EXTERNAL_USER) {
          console.info('[notifications] Ignored external-user event', getNotificationLogContext(event, data));
          return;
        }
        set((state) => {
          if (
            !isVisualNotification(notification) ||
            event === NOTIFICATION_EVENTS.MEET_SMARTCOACH_INSIGHT
          ) {
            console.info('[notifications] Kept notification internal', {
              ...getNotificationLogContext(event, data),
              visual: false,
            });
            return {
              lastNotification: notification,
            };
          }

          const newNotifications = [notification, ...state.notifications].slice(0, MAX_NOTIFICATIONS);
          console.info('[notifications] Added notification to visual list', {
            ...getNotificationLogContext(event, data),
            visual: true,
            visibleCount: newNotifications.length,
          });
          return {
            lastNotification: notification,
            notifications: newNotifications,
            unreadCount: calculateUnreadCount(newNotifications),
          };
        });
      });

      set({ socket });
    },

    subscribeToRoom: (userId: string, roomId: string) => {
      const normalizedRoomId = roomId.trim();
      if (!userId || !normalizedRoomId) return;
      const { socket } = get();
      if (roomSubscription && (roomSubscription.userId !== userId || roomSubscription.roomId !== normalizedRoomId) && socket) {
        unregisterRoom(socket, roomSubscription.userId, roomSubscription.roomId);
      }
      roomSubscription = { userId, roomId: normalizedRoomId };
      if (socket) registerRoom(socket, userId, normalizedRoomId);
    },

    unsubscribeFromRoom: (userId: string, roomId: string) => {
      if (!roomSubscription || roomSubscription.userId !== userId || roomSubscription.roomId !== roomId) return;
      const { socket } = get();
      if (socket) unregisterRoom(socket, userId, roomId);
      roomSubscription = null;
      stopHeartbeat();
      set({ isRegistered: false });
    },

    disconnect: () => {
      const { socket, isRegistered } = get();
      stopHeartbeat();

      if (socket) {
        if (isRegistered && roomSubscription) {
          unregisterRoom(socket, roomSubscription.userId, roomSubscription.roomId);
        }
        socket.disconnect();
      }

      roomSubscription = null;

      set({
        socket: null,
        status: 'disconnected',
        isRegistered: false,
      });
    },

    dismissNotification: (id: string) => {
      set((state) => {
        const newNotifications = state.notifications.map((n) =>
          n.id === id ? { ...n, dismissed: true } : n,
        );
        return {
          notifications: newNotifications,
          unreadCount: calculateUnreadCount(newNotifications),
        };
      });
    },

    clearNotifications: () => {
      set({ notifications: [], lastNotification: null, unreadCount: 0 });
    },

    addPendingAccessRequests: (
      roomId: string,
      participants: { id: string; name: string; email?: string | null }[],
    ) => {
      const { notifications } = get();

      const existingRequesterIds = new Set<string>();
      notifications.forEach((n) => {
        if (n.event === NOTIFICATION_EVENTS.MEET_ENTER_REQUEST) {
          const reqId = (n.parsedMessage as MeetEnterRequestMessage)?.requesterId;
          if (reqId) existingRequesterIds.add(reqId);
        }
      });

      const newItems: NotificationItem[] = [];

      for (const p of participants) {
        if (!p.id || existingRequesterIds.has(p.id)) continue;

        const notifId = `pending-request-${roomId}-${p.id}`;
        if (notifications.some((n) => n.id === notifId)) continue;

        const requesterName = p.name || p.email || 'Participante';
        const parsedMessage: MeetEnterRequestMessage = {
          roomId,
          requesterId: p.id,
          requesterName,
          roomName: null,
          message: `${requesterName} pediu para entrar na sala`,
        };

        const payload: BaseNotificationPayload = {
          id: notifId,
          title: 'Solicitação de entrada',
          message: JSON.stringify(parsedMessage),
          eventName: NOTIFICATION_EVENTS.MEET_ENTER_REQUEST,
          type: 'ACTION',
          priority: 'HIGH',
          senderId: p.id,
          senderName: requesterName,
          category: 'ROOM',
          recipients: [],
          status: 'UNREAD',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        newItems.push({
          id: notifId,
          event: NOTIFICATION_EVENTS.MEET_ENTER_REQUEST,
          payload,
          parsedMessage,
          timestamp: new Date(),
          dismissed: false,
        });

        existingRequesterIds.add(p.id);
      }

      if (newItems.length === 0) return 0;

      set((state) => {
        const updated = [...newItems, ...state.notifications].slice(0, MAX_NOTIFICATIONS);
        return {
          notifications: updated,
          unreadCount: calculateUnreadCount(updated),
        };
      });

      return newItems.length;
    },
  };
});

// Selectors (para performance - evita re-renders desnecessÃƒÂ¡rios)
export const selectConnectionStatus = (state: NotificationState) => state.status;
export const selectIsConnected = (state: NotificationState) => state.status === 'connected';
export const selectLastNotification = (state: NotificationState) => state.lastNotification;
export const selectNotifications = (state: NotificationState) => state.notifications;
export const selectUnreadCount = (state: NotificationState) => state.unreadCount;
